/**
 * Box — one announcement, one box in the stream. It OBEYS render (it never
 * decides file-vs-component): render:@file embeds the artifact as-is (no
 * preview, no conversion); render:@browser renders a live primitive / page /
 * interactive web-component. Every box carries the three actions: reply,
 * download (as-is), control (open its @ref** / what it used).
 */
import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Reply, Download, Settings2, Eye, Link as LinkIcon, Star, StarOff, Maximize2, Minimize2, CircleFadingArrowUp, type LucideIcon } from "lucide-react";

import { Tooltip } from "../components/Tooltip";
import { ArtifactRenderer, type HydrateArtifact } from "./ArtifactRenderer";
import type { ArtifactInteractionKind } from "./artifactInteraction";
import { artifactMachineIdOf, artifactMachineStatusOf, payloadArtifactOf, renderArtifactOf } from "./artifactMachine";
import { artifactUpgradeStore } from "./artifactUpgradeStore";
import type { Announcement, BoxAction, RenderFile } from "./contract";
import { isAuthAnnouncement, serverIdOf } from "./contract";
import { artifactKindOf, toneForArtifactKind, toneForNotification } from "./tones";
import { humanizeChannelTime } from "./time";
import type { ChannelApi } from "./useChannel";

interface BoxProps {
  a: Announcement;
  onAction: (action: BoxAction, a: Announcement) => void;
  accent?: string;
  hydrateArtifact?: HydrateArtifact;
  artifactSurfaceSuppressed?: boolean;
  onArtifactInteraction?: (interaction: ArtifactInteractionKind, a: Announcement, detail?: Record<string, unknown>) => void | Promise<unknown>;
  // the control tab's conversation-context read (the artifact's OWN
  // conversation, id == the artifact machine id) — threaded from the channel.
  query?: ChannelApi["query"];
  // the HUMAN-session lifecycle cancel announce (sci//lifecycle:cancel::by:
  // @ref** on the ref lane) — never an artifact bearer.
  onCancelArtifact?: (a: Announcement) => Promise<void>;
  // the ARTIFACT SCOPE TOGGLE (control tab, default HUMAN ⇄ ORG): the
  // lifecycle rescope announce — the world re-homes the row; the client only
  // emits intent and re-reads the world truth.
  onShareArtifact?: (a: Announcement, mode: "with" | "exclude") => Promise<void>;
}

// A plain chat message's text: the message ARTIFACT carries it as input.text
// (the artifact IS the message — the shape history reads serve); the live
// evidence package carries the same text in the evidence lane data. The
// lanes ride flat on the announcement itself (THE FLATTEN).
const msgText = (payload: unknown): string => {
  const p = payload as { artifact?: unknown[]; evidence?: unknown[] } | null;
  const art = Array.isArray(p?.artifact) ? p.artifact[0] as any : null;
  const artText = art?.input?.text; // presence — the type noun is dead
  if (typeof artText === "string" && artText) return artText;
  const ev = Array.isArray(p?.evidence) ? p.evidence[0] as any : null;
  const body = ev?.body;
  const data = body?.data;
  const t = body?.text ?? body?.message ?? data?.text ?? data?.message;
  return typeof t === "string" ? t : "";
};
export const renderText = (payload: unknown): string => {
  const p = payload as { text?: unknown; message?: unknown; result?: unknown } | null;
  const t = p?.text ?? p?.message ?? p?.result;
  return typeof t === "string" ? t : msgText(payload);
};

// ── wrapper controls + evidence tags ───────────────────────────────────────
const SENDER_TONE: Record<string, string> = {
  you: "#10b981", workflow: "#6366f1", agent: "#a21caf", control: "#0891b2",
};
const senderLabel = (sender: string) => sender === "human" ? "You" : sender;
const senderTone = (sender: string): string => SENDER_TONE[sender === "human" ? "you" : sender] || "#9ca3af";

// top-right actions — CONTROL stays centered as a view ⇄ control toggle.
const ACTIONS: ReadonlyArray<{ key: BoxAction; Icon: LucideIcon; label: string; description: string }> = [
  { key: "download", Icon: Download, label: "Download", description: "Download this artifact as-is" },
  { key: "reply", Icon: Reply, label: "Reply", description: "Reply with this artifact as context" },
];

const CONTROL_MODES = [
  { on: false, label: "View", description: "Show the artifact surface", Icon: Eye },
  { on: true, label: "Control", description: "Show downloads, interactions, permission, and refs", Icon: Settings2 },
];

// THE HARNESS PANE LIVES IN THE ARTIFACT (Ori 2026-08-03): the world-owned
// interactive wrapper renders it from the artifact's own served copy and keeps
// it live through the artifact bearer — the client renders artifact html only
// and carries NO terminal chrome/store/plumbing of its own.

// the CONTROL face of an artifact (view ⇄ control toggle): the box's context —
// its refs/recipients/render PLUS the artifact's OWN world-claimed activity
// (downloads tally, interaction counts, permission projection, lifecycle
// cancel), fetched from the artifact's per-surface conversation. Inline, NOT
// a side modal.
const controlSurface: React.CSSProperties = {
  display: "grid",
  gap: 10,
  padding: 12,
  borderRadius: 9,
  border: "1px solid var(--color-border, #2f2f2f)",
  background: "color-mix(in srgb, var(--color-surface, #1b1b1b) 72%, transparent)",
  minWidth: 0,
};

const controlChip: React.CSSProperties = {
  display: "inline-flex",
  minWidth: 0,
  alignItems: "center",
  gap: 6,
  padding: "7px 9px",
  borderRadius: 7,
  border: "1px solid var(--color-border, #2f2f2f)",
  background: "var(--color-bg, #111)",
  color: "var(--color-text-primary, #f3f3f3)",
  fontSize: 11,
  lineHeight: 1,
};

const controlLabel: React.CSSProperties = {
  flexShrink: 0,
  color: "var(--color-text-secondary, #9ca3af)",
  fontSize: 9,
  fontWeight: 850,
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

const controlValue: React.CSSProperties = {
  minWidth: 0,
  overflow: "visible",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  fontWeight: 500,
};

const ControlChip: React.FC<{ label: string; value?: string; title?: string }> = ({ label, value, title }) => {
  if (!value) return null;
  return (
    <span style={controlChip} title={title || value}>
      <span style={controlLabel}>{label}</span>
      <span style={controlValue}><ProtocolInline value={value} /></span>
    </span>
  );
};

const ControlList: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <section style={{ display: "grid", gap: 6, minWidth: 0 }}>
    <div style={controlLabel}>{label}</div>
    <div style={{ display: "grid", gap: 5, minWidth: 0 }}>{children}</div>
  </section>
);

const ControlRow: React.FC<{ primary: string; secondary?: string; title?: string }> = ({ primary, secondary, title }) => (
  <div
    title={title || [primary, secondary].filter(Boolean).join(" · ")}
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      alignItems: "center",
      minWidth: 0,
      padding: "8px 9px",
      borderRadius: 7,
      background: "color-mix(in srgb, var(--color-bg, #111) 88%, transparent)",
    }}
  >
    <span style={{ ...controlValue, color: "var(--color-text-primary, #f3f3f3)" }}>
      <ProtocolInline value={primary} />
    </span>
    {secondary && (
      <span style={{ color: "var(--color-text-secondary, #9ca3af)", fontSize: 10, fontWeight: 650 }}>
        <ProtocolInline value={secondary} subtle />
      </span>
    )}
  </div>
);

const splitTopLevel = (value: string, delimiter: string): string[] => {
  const out: string[] = [];
  let current = "";
  let depth = 0;
  let quote = "";
  for (const char of value) {
    if (quote) {
      current += char;
      if (char === quote) quote = "";
      continue;
    }
    if (char === "'" || char === "\"") {
      quote = char;
      current += char;
      continue;
    }
    if (char === "(") depth += 1;
    if (char === ")") depth = Math.max(0, depth - 1);
    if (char === delimiter && depth === 0) {
      out.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  out.push(current);
  return out.map((part) => part.trim()).filter(Boolean);
};

const protocolOf = (value: string): { protocol: string; rest: string } | null => {
  const protocols = ["sci**::", "sci*//", "sci::", "sci//"];
  const protocol = protocols.find((candidate) => value.startsWith(candidate));
  return protocol ? { protocol, rest: value.slice(protocol.length) } : null;
};

const displayToken = (value: string): string => {
  const list = value.endsWith("**");
  const raw = list ? value.slice(0, -2) : value;
  const unwrapped = raw.startsWith("@(") && raw.endsWith(")")
    ? raw.slice(2, -1)
    : raw.startsWith("@")
      ? raw.slice(1)
      : raw;
  const quoted = unwrapped.startsWith("'") && unwrapped.endsWith("'") ? unwrapped.slice(1, -1) : unwrapped;
  return `${quoted.replace(/[-_]+/g, " ")}${list ? "[]" : ""}`;
};

const tokenParts = (value: string): string[] => {
  const protocol = protocolOf(value);
  if (protocol) {
    return [
      protocol.protocol,
      ...splitTopLevel(protocol.rest, ":").flatMap((part) => {
        const typed = part.startsWith("@(") && part.endsWith(")") ? part.slice(2, -1) : "";
        return typed ? splitTopLevel(typed, ":").map(displayToken) : [displayToken(part)];
      }),
    ].filter(Boolean);
  }
  if (value.includes(" · ")) return value.split(" · ").flatMap((part) => tokenParts(part.trim()));
  if (/[::_/-]/.test(value)) return value.split(/(::|:|_|-|\/)/).filter((part) => part && !["::", ":", "_", "-", "/"].includes(part)).map(displayToken);
  return [displayToken(value)];
};

const atomChip: React.CSSProperties = {
  display: "inline-flex",
  maxWidth: "100%",
  padding: "3px 6px",
  borderRadius: 6,
  border: "1px solid color-mix(in srgb, var(--color-border, #2f2f2f) 86%, transparent)",
  background: "color-mix(in srgb, var(--color-surface, #1b1b1b) 78%, transparent)",
  color: "inherit",
  lineHeight: 1.05,
  overflowWrap: "anywhere",
};

const ProtocolInline: React.FC<{ value?: string; subtle?: boolean }> = ({ value = "", subtle = false }) => {
  const parts = tokenParts(String(value)).filter(Boolean);
  if (!parts.length) return null;
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, minWidth: 0 }}>
      {parts.map((part, index) => (
        <span
          key={`${part}:${index}`}
          style={{
            ...atomChip,
            opacity: subtle ? 0.78 : 1,
            fontSize: part.startsWith("sci") ? 9 : undefined,
            color: part.startsWith("sci") ? "var(--accent-primary, #6B7EEA)" : undefined,
          }}
        >
          {part}
        </span>
      ))}
    </span>
  );
};

const renderControlLabel = (rf: RenderFile): string => (
  rf.suffix ? `${rf.mode}:${rf.suffix}` : rf.primitive ? `${rf.mode}:${rf.primitive}` : rf.mode
);

// ── the artifact CONTROL data — the artifact's OWN world-claimed activity ────
// Every registered interactive artifact owns a conversation whose id == its
// machine id (the per-surface conversation law). Three world reads feed the
// control tab, split by WHERE the row lives:
//
//  • INTERACTIONS ride the conversation HISTORY (they carry an artifact shell,
//    so world's conversation_context_rows serve returns them): read via
//    query({ conversationId }) and group by the ref-lane
//    sci//artifact:interaction:<kind> atom.
//  • DOWNLOADS (sci//artifact:downloaded:@artifact**, evidence
//    {artifact,count,last_at}) and PERMISSION (sci//artifact:permission:@permission**,
//    evidence {artifact,hash,signed_at,expired_at} — the flat hash of the
//    org-KMS signed secret, NEVER a secret value) carry artifact: [] (empty),
//    so world's history serve EXCLUDES them (renderable content only). They
//    ride a REFS-mode query instead (kind=announcement; the outlet scopes it
//    to the caller's human+org), and we filter by evidence[0].artifact.
const ARTIFACT_DOWNLOADED_REF = "sci//artifact:downloaded:@artifact**";
const ARTIFACT_PERMISSION_REF = "sci//artifact:permission:@permission**";
const ARTIFACT_INTERACTION_REF_PREFIX = "sci//artifact:interaction:";

type QueryResult = Awaited<ReturnType<ChannelApi["query"]>>;

// the refs feed drops empty-artifact rows from `items`, so the downloads /
// permission rows come back only on the query result's `raw` lane — the
// un-normalized world forms (row.body.{ref,evidence}) keyed under results.*.rows.
const rawRowsOf = (res: QueryResult): Array<Record<string, unknown>> => {
  const results = (res?.raw as { results?: Record<string, { rows?: unknown[] }> } | undefined)?.results;
  if (!results || typeof results !== "object") return [];
  const rows: Array<Record<string, unknown>> = [];
  for (const bucket of Object.values(results)) {
    if (Array.isArray(bucket?.rows)) {
      for (const row of bucket.rows) if (row && typeof row === "object") rows.push(row as Record<string, unknown>);
    }
  }
  return rows;
};

const rawEvidenceOf = (row: Record<string, unknown>): Record<string, unknown> => {
  const body = row.body && typeof row.body === "object" ? row.body as Record<string, unknown> : row;
  const evidence = Array.isArray(body.evidence)
    ? body.evidence
    : Array.isArray((row as { evidence?: unknown[] }).evidence) ? (row as { evidence: unknown[] }).evidence : [];
  const first = evidence[0];
  return first && typeof first === "object" ? first as Record<string, unknown> : {};
};

const downloadsFor = (rows: Array<Record<string, unknown>>, machineId: string): { count: number; lastAt: string } | undefined => {
  const match = rows.map(rawEvidenceOf).find((e) => String(e.artifact || "") === machineId && Number(e.count) > 0);
  return match ? { count: Number(match.count), lastAt: String(match.last_at || "") } : undefined;
};

const permissionFor = (rows: Array<Record<string, unknown>>, machineId: string): { hash: string; signedAt: string; expiredAt: string } | undefined => {
  const match = rows.map(rawEvidenceOf).find((e) => String(e.artifact || "") === machineId && !!e.hash);
  return match
    ? { hash: String(match.hash), signedAt: String(match.signed_at || ""), expiredAt: String(match.expired_at || "") }
    : undefined;
};

const interactionsFor = (history: Announcement[]): Array<{ kind: string; count: number; lastAt: string }> => {
  const grouped = new Map<string, { kind: string; count: number; lastAt: string }>();
  for (const row of history) {
    const refAtoms = (row.ref ?? []).filter((r): r is string => typeof r === "string");
    const kindAtom = refAtoms.find((r) => r.startsWith(ARTIFACT_INTERACTION_REF_PREFIX));
    if (!kindAtom) continue;
    const kind = kindAtom.slice(ARTIFACT_INTERACTION_REF_PREFIX.length);
    const entry = grouped.get(kind);
    if (!entry) grouped.set(kind, { kind, count: 1, lastAt: row.timestamp });
    else {
      entry.count += 1;
      if (row.timestamp > entry.lastAt) entry.lastAt = row.timestamp;
    }
  }
  return [...grouped.values()].sort((x, y) => y.count - x.count);
};

type ArtifactControlData = {
  downloads?: { count: number; lastAt: string };
  permission?: { hash: string; signedAt: string; expiredAt: string };
  interactions: Array<{ kind: string; count: number; lastAt: string }>;
  // WHO the artifact is SHARED with — read back from the world row (the
  // grant rides its refs). Absent = shared with nobody, which is the default:
  // an artifact is private to the scope it was created in until a human
  // shares it. The row is truth; this is display only.
  shared?: boolean;
};

const ARTIFACT_SHARE_REF = "sci//lifecycle:share::with:@ref**";

const sharedFor = (rows: Array<Record<string, unknown>>, machineId: string): boolean | undefined => {
  // SHARING GRANTS, IT NEVER MOVES: the artifact keeps its storage key and its
  // scope; the grant rides its refs. The atom IS the declaration.
  for (const row of rows) {
    const address = String(row.address ?? row.key ?? "");
    if (!address.endsWith(`::artifact:${machineId}`)) continue;
    const refs = ([] as unknown[]).concat(
      Array.isArray(row.ref) ? row.ref : [],
      Array.isArray(row.refs) ? row.refs : [],
    ).map(String);
    if (refs.includes("sci//lifecycle:share::exclude:@ref**")) return false;
    if (refs.includes(ARTIFACT_SHARE_REF)) return true;
  }
  return undefined;
};

type ArtifactControlState = {
  status: "loading" | "ready" | "error";
  error: string;
  data: ArtifactControlData;
};

const ControlView: React.FC<{
  a: Announcement;
  artifactMachineId: string;
  query?: ChannelApi["query"];
  onCancelArtifact?: (a: Announcement) => Promise<void>;
  onShareArtifact?: (a: Announcement, mode: "with" | "exclude") => Promise<void>;
}> = ({ a, artifactMachineId, query, onCancelArtifact, onShareArtifact }) => {
  const notifyReason = a.notify?.reason || "";
  const [control, setControl] = useState<ArtifactControlState>({ status: "loading", error: "", data: { interactions: [] } });
  const [cancelState, setCancelState] = useState<{ busy: boolean; error: string; sent: boolean }>({ busy: false, error: "", sent: false });
  const [scopeState, setScopeState] = useState<{ busy: boolean; error: string }>({ busy: false, error: "" });
  const [refreshKey, setRefreshKey] = useState(0);
  // this view mounts when the control tab opens — issue the THREE reads right
  // then (interactions off the artifact's own conversation history; downloads +
  // permission off their refs-mode queries), error-loud: a failed read renders
  // as the error state, never as an empty activity surface.
  useEffect(() => {
    if (!artifactMachineId || !query) return;
    let alive = true;
    setControl({ status: "loading", error: "", data: { interactions: [] } });
    Promise.all([
      query({ conversationId: artifactMachineId, limit: 18 }),
      query({ refs: [ARTIFACT_DOWNLOADED_REF], kind: "announcement" }),
      query({ refs: [ARTIFACT_PERMISSION_REF], kind: "announcement" }),
      query({ refs: [ARTIFACT_SHARE_REF], kind: "artifact" }),
    ])
      .then(([historyRes, downloadsRes, permissionRes, shareRes]) => {
        if (!alive) return;
        const history = Array.isArray(historyRes.conversationContext?.history) ? historyRes.conversationContext.history : [];
        const downloads = downloadsFor(rawRowsOf(downloadsRes), artifactMachineId);
        const permission = permissionFor(rawRowsOf(permissionRes), artifactMachineId);
        const shared = sharedFor(rawRowsOf(shareRes), artifactMachineId);
        setControl({
          status: "ready",
          error: "",
          data: {
            ...(downloads ? { downloads } : {}),
            ...(permission ? { permission } : {}),
            ...(shared === undefined ? {} : { shared }),
            interactions: interactionsFor(history),
          },
        });
      })
      .catch((error) => {
        if (!alive) return;
        setControl({
          status: "error",
          error: error instanceof Error ? error.message : String(error),
          data: { interactions: [] },
        });
      });
    return () => { alive = false; };
  }, [artifactMachineId, query, refreshKey]);
  const cancelArtifact = async () => {
    if (!onCancelArtifact || cancelState.busy) return;
    setCancelState({ busy: true, error: "", sent: false });
    try {
      await onCancelArtifact(a);
      setCancelState({ busy: false, error: "", sent: true });
      setRefreshKey((key) => key + 1);
    } catch (error) {
      setCancelState({ busy: false, error: error instanceof Error ? error.message : String(error), sent: false });
    }
  };
  const shareArtifact = async (mode: "with" | "exclude") => {
    if (!onShareArtifact || scopeState.busy) return;
    setScopeState({ busy: true, error: "" });
    try {
      await onShareArtifact(a, mode);
      setScopeState({ busy: false, error: "" });
      // re-read the world truth — the re-homed row is the persistence proof.
      setRefreshKey((key) => key + 1);
    } catch (error) {
      setScopeState({ busy: false, error: error instanceof Error ? error.message : String(error) });
    }
  };
  const showArtifactControl = !!artifactMachineId && !!query;
  return (
    <div style={controlSurface}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
        <ControlChip label="from" value={senderLabel(a.sender)} />
        <ControlChip label="kind" value={a.kind} />
        <ControlChip label="conv" value={a.conversationId} />
      </div>

      {a.notify && (
        <ControlList label="notification">
          <ControlRow primary={a.notify.level} secondary={notifyReason || undefined} title={a.notify.reason} />
        </ControlList>
      )}

      {a.refs.length > 0 && (
        <ControlList label="refs">
          {a.refs.map((r, i) => (
            <ControlRow
              key={i}
              primary={r.label || r.path}
              secondary={r.kind || undefined}
              title={r.path}
            />
          ))}
        </ControlList>
      )}

      {a.recipient.length > 0 && (
        <ControlList label="recipients">
          {a.recipient.map((r, i) => <ControlRow key={i} primary={r.label || r.path} title={r.path} />)}
        </ControlList>
      )}

      {a.render.length > 0 && (
        <ControlList label="render">
          {a.render.map((rf, i) => (
            <ControlRow
              key={i}
              primary={renderControlLabel(rf)}
              secondary={rf.machine?.status}
              title={rf.machine?.label || rf.machine?.name || renderControlLabel(rf)}
            />
          ))}
        </ControlList>
      )}

      {showArtifactControl && control.status === "error" && (
        <ControlList label="artifact activity">
          <ControlRow primary={`Control data unavailable. ${control.error}`} title={control.error} />
        </ControlList>
      )}
      {showArtifactControl && control.status === "loading" && (
        <ControlList label="artifact activity">
          <ControlRow primary="Loading artifact activity..." />
        </ControlList>
      )}
      {showArtifactControl && control.status === "ready" && (
        <>
          <ControlList label="downloads">
            {control.data.downloads
              ? (
                <ControlRow
                  primary={`${control.data.downloads.count} download${control.data.downloads.count === 1 ? "" : "s"}`}
                  secondary={control.data.downloads.lastAt ? humanizeChannelTime(control.data.downloads.lastAt) : undefined}
                  title={control.data.downloads.lastAt || undefined}
                />
              )
              : <ControlRow primary="No downloads recorded" />}
          </ControlList>
          <ControlList label="interactions">
            {control.data.interactions.length > 0
              ? control.data.interactions.map((interaction) => (
                <ControlRow
                  key={interaction.kind}
                  primary={`${interaction.kind} × ${interaction.count}`}
                  secondary={interaction.lastAt ? humanizeChannelTime(interaction.lastAt) : undefined}
                  title={interaction.lastAt || undefined}
                />
              ))
              : <ControlRow primary="No interactions recorded" />}
          </ControlList>
          <ControlList label="share">
            {/* THE ARTIFACT SHARE: the artifact STAYS in the scope it was
                created in and is GRANTED to a subject — sharing is not moving.
                The announce declares, the WORLD claims the grant, this view
                re-reads the row (never local state). */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <ControlChip
                label="shared"
                value={control.data.shared ? "yes" : "private (default)"}
              />
              {onShareArtifact && (["with", "exclude"] as const).map((target) => (
                <button
                  key={target}
                  type="button"
                  onClick={() => shareArtifact(target)}
                  disabled={scopeState.busy || (control.data.shared ?? false) === (target === "with")}
                  style={{
                    ...iconButtonStyle,
                    width: "fit-content",
                    padding: "4px 10px",
                    opacity: scopeState.busy || (control.data.shared ?? false) === (target === "with") ? 0.5 : 1,
                  }}
                >
                  {scopeState.busy ? "…" : target === "with" ? "Share" : "Unshare"}
                </button>
              ))}
            </div>
            {scopeState.error && (
              <ControlRow primary={`Share failed. ${scopeState.error}`} title={scopeState.error} />
            )}
          </ControlList>
          <ControlList label="lifecycle">
            {control.data.permission
              ? (
                // the permission row's FLAT projection (hash + times) — the
                // signed/live proof. Read-only; no secret value exists here.
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  <ControlChip label="hash" value={control.data.permission.hash} />
                  {control.data.permission.signedAt && (
                    <ControlChip label="signed" value={humanizeChannelTime(control.data.permission.signedAt)} title={control.data.permission.signedAt} />
                  )}
                  {control.data.permission.expiredAt && (
                    <ControlChip label="expires" value={humanizeChannelTime(control.data.permission.expiredAt)} title={control.data.permission.expiredAt} />
                  )}
                </div>
              )
              : <ControlRow primary="No signed permission recorded" />}
            {cancelState.sent && (
              <ControlRow primary="Cancel sent" secondary="the stored secret is retired; outstanding tokens die at the next verify" />
            )}
            {cancelState.error && (
              <ControlRow primary={`Cancel failed. ${cancelState.error}`} title={cancelState.error} />
            )}
            {onCancelArtifact && (
              <button
                type="button"
                onClick={cancelArtifact}
                disabled={cancelState.busy}
                style={{
                  ...iconButtonStyle,
                  width: "fit-content",
                  padding: "6px 10px",
                  border: "1px solid var(--status-danger-border)",
                  color: "var(--status-danger-text)",
                  opacity: cancelState.busy ? 0.6 : 1,
                }}
              >
                {cancelState.busy ? "Cancelling..." : "Cancel artifact"}
              </button>
            )}
          </ControlList>
        </>
      )}
    </div>
  );
};

// artifact:status:@status — the per-artifact status tag (lifecycle is
// artifact:@machine:@binary). `fail:block` is access
// denial; other fail states can surface retry.
const STATUS_LABEL: Record<string, string> = {
  start: "sending",
  complete: "synced",
  "fail:start": "send failed",
  "fail:complete": "failed",
  "fail:block": "blocked",
  retry: "retrying",
};
const STATUS_TONE: Record<string, string> = {
  start: "var(--status-info-text)",
  complete: "var(--status-success-text)",
  "fail:start": "var(--status-danger-text)",
  "fail:complete": "var(--status-danger-text)",
  "fail:block": "var(--status-danger-text)",
  retry: "var(--status-warning-text)",
};
const statusTone = (status: string): string =>
  STATUS_TONE[status] || "var(--color-text-secondary)";
const metaTagStyle: React.CSSProperties = {
  minWidth: 0,
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  gap: 4,
  padding: "7px 8px",
  minHeight: 46,
  borderRadius: 7,
  border: "1px solid var(--color-border, #2f2f2f)",
  background: "color-mix(in srgb, var(--color-surface, #1b1b1b) 84%, transparent)",
  color: "var(--color-text-secondary, #b8b8b8)",
  lineHeight: 1.1,
  whiteSpace: "normal",
  overflow: "visible",
};

const metaTagLabelStyle: React.CSSProperties = {
  maxWidth: "100%",
  color: "var(--accent-primary, #6B7EEA)",
  fontSize: 9,
  fontWeight: 900,
  lineHeight: 1,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const metaTagValueStyle: React.CSSProperties = {
  maxWidth: "100%",
  color: "var(--color-text-primary, #f3f3f3)",
  fontSize: "clamp(9px, 2cqw, 11px)",
  fontWeight: 500,
  lineHeight: 1.12,
  overflowWrap: "anywhere",
};

const iconButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  border: "none",
  background: "transparent",
  color: "var(--color-text-secondary, #b8b8b8)",
  borderRadius: 7,
  cursor: "pointer",
};

const SUPPRESSED_ARTIFACT_TITLE = "Shown in stream";
const SUPPRESSED_ARTIFACT_BODY = "Still prioritized here.";

const suppressedArtifactStyle: React.CSSProperties = {
  minHeight: 118,
  margin: "0 12px",
  padding: 18,
  display: "grid",
  alignContent: "center",
  gap: 7,
  borderRadius: 9,
  border: "1px solid var(--color-border, #2f2f2f)",
  background: "color-mix(in srgb, var(--color-surface, #1b1b1b) 52%, transparent)",
  color: "var(--color-text-secondary, #b8b8b8)",
};

const suppressedArtifactTitleStyle: React.CSSProperties = {
  color: "var(--color-text-primary, #f3f3f3)",
  fontSize: 13,
  fontWeight: 800,
  lineHeight: 1.2,
};

const suppressedArtifactBodyStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: 1.35,
};

const StatusTag: React.FC<{ status: string; onRetry: () => void }> = ({ status, onRetry }) => (
  <span style={metaTagStyle} data-artifact-status={status}>
    <span style={{ ...metaTagLabelStyle, color: statusTone(status) }}>
      Status
    </span>
    <span style={{ ...metaTagValueStyle, color: statusTone(status), textTransform: "uppercase", letterSpacing: 0.3 }}>
      {STATUS_LABEL[status] || status}
    </span>
    {status.startsWith("fail:") && status !== "fail:block" && (
      <button type="button" onClick={onRetry} title="Retry the failed sync"
        style={{ fontSize: 11, padding: "1px 8px", border: `1px solid ${statusTone(status)}`, borderRadius: 6, background: "transparent", color: statusTone(status), cursor: "pointer" }}>
        Retry
      </button>
    )}
  </span>
);

// A label for one hydrated edge entry — handles BOTH shapes: the optimistic
// {ref,label} (client-side) and the world-hydrated full announcement row
// ({id, kind, sender, body:{…}}). Defensive — never throws on a bare ref.
const edgeLabel = (e: any): string => {
  if (e == null) return "";
  if (typeof e === "number" || typeof e === "string") return `#${e}`;
  const b = e.body || {};
  const t = e.label || b.text || renderText(b) || b.recipient || e.sender || e.kind || (e.id != null ? `#${e.id}` : "");
  return String(t).slice(0, 48);
};

const edgeChip: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 8,
  border: "1px solid var(--color-border, #e5e7eb)", background: "var(--color-surface, #f8f9fa)",
  fontSize: 11, color: "var(--color-text-secondary, #6b7280)", maxWidth: 280,
};

// The reactive edges of a TREE node, shown ABOVE the body:
//   ↩ reply  — announcement:reply:@announcement** (what this replies to)
//   ⚡ next   — announcement:next:@conditional** (IF a match holds THEN refs; conditional:then:@ref**)
//   ⏱ when   — announcement:when:@subscribe** (a temporal/data subscription)
const subLabel = (s: any): string => {
  if (s == null) return "";
  if (typeof s !== "object") return String(s);
  return String(s.interval || s.next || s.machine || "subscription").slice(0, 40);
};
const ReplyThenStrip: React.FC<{ a: Announcement }> = ({ a }) => {
  const reply = (a.reply ?? []) as any[];
  const conds = (a.next ?? []) as any[];         // conditionals {if:[match], then:[ref]}
  const subs = (a.when ?? []) as any[];          // subscriptions {machine,binary,next,interval}
  if (!reply.length && !conds.length && !subs.length) return null;
  const ellipsis: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
      {reply.map((r, i) => (
        <span key={`r${i}`} style={edgeChip} title="replying to (announcement:reply)">
          <span style={{ opacity: 0.6 }}>↩</span><span style={ellipsis}>{edgeLabel(r)}</span>
        </span>
      ))}
      {conds.map((c, i) => (
        <span key={`c${i}`} style={edgeChip} title="if a match holds, continue to refs (announcement:next → conditional:then:@ref)">
          <span style={{ opacity: 0.6 }}>⚡</span><span style={ellipsis}>if {edgeLabel((c?.if ?? [])[0])}</span>
        </span>
      ))}
      {subs.map((s, i) => (
        <span key={`s${i}`} style={edgeChip} title="subscription — fires next, repeats per interval (announcement:when:@subscribe)">
          <span style={{ opacity: 0.6 }}>⏱</span><span style={ellipsis}>{subLabel(s)}</span>
        </span>
      ))}
    </div>
  );
};

export const Box: React.FC<BoxProps> = ({ a, onAction, accent: accentOverride, hydrateArtifact, artifactSurfaceSuppressed = false, onArtifactInteraction, query, onCancelArtifact, onShareArtifact }) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const hoverTrackedRef = useRef(false);
  const clickTrackedRef = useRef(false);
  const title = a.refs[a.refs.length - 1]?.label || a.machine?.label || a.render[0]?.suffix || a.kind;
  // Each box is an ARTIFACT CARD — a responsive card with a MIN size (readable on a
  // phone, capped on a laptop), NOT a hug-content chat bubble. SENT (sender:human)
  // sits on the RIGHT, GET (agent/workflow/control) on the LEFT. A data artifact
  // (file/table/report) gets a wider card.
  const sent = a.sender === "human";
  const payloadArtifact = payloadArtifactOf(a);
  const artifactMachineId = artifactMachineIdOf(renderArtifactOf(a));
  const renderKind = a.render[0]?.primitive || a.render[0]?.suffix || a.kind;
  const accent = accentOverride || (payloadArtifact
    ? toneForArtifactKind(artifactKindOf(payloadArtifact, renderKind)).accent
    : a.notify
      ? toneForNotification(a.notify.level).accent
      : toneForArtifactKind(renderKind).accent);
  // bigger responsive % — the card fills a real share of the page (doesn't leave a
  // huge empty gutter), min-readable on phone, capped on a wide laptop.
  const sizing: React.CSSProperties = {
    width: "100%",
    containerType: "inline-size",
    [sent ? "borderRight" : "borderLeft"]: `3px solid ${accent}`,
  };
  // view ⇄ control toggle — per artifact, inline (no docked modal). The toggle lives
  // in the header CENTER (between sender and datetime).
  const [showControl, setShowControl] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [hydratedArtifact, setHydratedArtifact] = useState<unknown>(null);
  const prioritized = a.prioritized?.active === true;
  const PrioritizeIcon = prioritized ? StarOff : Star;
  const status = artifactMachineStatusOf(hydratedArtifact)
    || artifactMachineStatusOf(renderArtifactOf(a))
    || a.machine?.status
    || a.batch?.status;
  const metaTitle = `${senderLabel(a.sender)} · ${title}`;
  useEffect(() => {
    setHydratedArtifact(null);
  }, [artifactMachineId]);
  useEffect(() => {
    const syncFullscreen = () => setFullscreen(document.fullscreenElement === frameRef.current);
    document.addEventListener("fullscreenchange", syncFullscreen);
    syncFullscreen();
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);
  const toggleFullscreen = async () => {
    const frame = frameRef.current;
    if (!frame) return;
    if (document.fullscreenElement === frame) {
      await document.exitFullscreen();
      return;
    }
    await frame.requestFullscreen();
  };
  // THE DELIBERATE UPGRADE lives in the header icon strip, beside Download /
  // Reply — visible on View AND Control. Holding the current version is the
  // deliberate default: while the mark stands and the human does not click,
  // the held body keeps serving as THIS version (never auto-replaced, not a
  // cache accident). The click fires the @activated interaction (the world
  // recomposes this instance over its OWN standing reads from the CURRENT
  // template and re-judges its declared validations — the invariance is a
  // consequence of composing), then pulls on a bounded schedule until the
  // serve's upgrade face is current (the one mark owner clears it).
  useSyncExternalStore(artifactUpgradeStore.subscribe, artifactUpgradeStore.getSnapshot);
  const upgradeKnown = !!artifactMachineId && artifactUpgradeStore.known(artifactMachineId);
  const upgradeArtifact = () => {
    if (!artifactMachineId) return;
    const ack = onArtifactInteraction?.("@activated", a);
    const pull = () => {
      if (!artifactUpgradeStore.known(artifactMachineId)) return;
      // With the artifact surface mounted, refresh it in place through its
      // own interaction door. The Control tab REPLACES the surface, so the
      // querySelector-only pull was a silent no-op there ("clicked Upgrade,
      // nothing happened", 2026-08-09): without a mounted root the forced
      // download refreshes the held cache + mark directly, and the next
      // mount serves the recomposed body.
      const root = document.querySelector(
        `[data-artifact-render-root="${CSS.escape(artifactMachineId)}"]`,
      );
      if (root) {
        root.dispatchEvent(new CustomEvent("causit-artifact-interaction", {
          detail: { interaction: "refresh" },
        }));
        return;
      }
      void hydrateArtifact?.(payloadArtifact, { force: true }).catch(() => undefined);
    };
    // the world completes the recompose INSIDE the announce crossing, so the
    // resolved ack is the first pull's cue; the bounded schedule is the
    // backstop for a timed-out ack on a loaded stack (2026-08-08). The tail
    // reaches past a full Book recompose (~2.5 min of report walks — every
    // pull ending before it landed left the mark standing on a stale body,
    // 2026-08-09); each pull no-ops once the mark cleared.
    void Promise.resolve(ack).catch(() => undefined).then(pull);
    for (const delay of [10_000, 30_000, 60_000, 120_000, 240_000, 420_000]) {
      window.setTimeout(pull, delay);
    }
  };
  return (
    <div
      ref={frameRef}
      data-artifact-box="true"
      onMouseEnter={() => {
        if (hoverTrackedRef.current) return;
        hoverTrackedRef.current = true;
        onAction("hovered", a);
      }}
      onClickCapture={(event) => {
        if (clickTrackedRef.current) return;
        const target = event.target as HTMLElement | null;
        if (target?.closest("button")) return;
        clickTrackedRef.current = true;
        onAction("clicked", a);
      }}
      style={{
        border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 12,
        background: "var(--color-bg, #fff)", padding: 0, marginBottom: 12,
        overflow: "hidden",
        position: "relative",
        ...sizing,
      }}
    >
      {!fullscreen && <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(42px, 1fr) max-content minmax(64px, 1fr)",
        alignItems: "center",
        columnGap: 6,
        padding: "10px 12px 8px",
        minWidth: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 4, minWidth: 0 }}>
          <Tooltip
            label={fullscreen ? "Exit full screen" : "Full screen"}
            description={fullscreen ? "Return this artifact to the workspace" : "Show this artifact with the browser full screen API"}
            side="top"
          >
            <button
              type="button"
              aria-label={fullscreen ? "Exit full screen" : "Full screen artifact"}
              disabled={artifactSurfaceSuppressed}
              onClick={() => {
                if (artifactSurfaceSuppressed) return;
                onAction("opened", a);
                void toggleFullscreen().catch(() => undefined);
              }}
              style={{
                ...iconButtonStyle,
                opacity: artifactSurfaceSuppressed ? 0.42 : 1,
                cursor: artifactSurfaceSuppressed ? "default" : iconButtonStyle.cursor,
              }}
            >
              {fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          </Tooltip>
          <Tooltip
            label={prioritized ? "Unprioritize" : "Prioritize"}
            description={prioritized ? "Remove this artifact from the prioritized rail" : "Prioritize this artifact on the feed rail"}
            side="top"
          >
            <button
              type="button"
              aria-label={prioritized ? "Unprioritize artifact" : "Prioritize artifact"}
              onClick={() => onAction(prioritized ? "unprioritize" : "prioritize", a)}
              data-action={prioritized ? "unprioritize" : "prioritize"}
              data-artifact-id={artifactMachineId}
              style={{
                ...iconButtonStyle,
                background: prioritized ? "color-mix(in srgb, var(--accent-primary, #6B7EEA) 14%, transparent)" : "transparent",
                color: prioritized ? "var(--accent-primary, #6B7EEA)" : "var(--color-text-secondary, #b8b8b8)",
              }}
            >
              <PrioritizeIcon size={15} />
            </button>
          </Tooltip>
          {isAuthAnnouncement(a) && (
            <Tooltip
              label="Connect"
              description={`Authenticate ${serverIdOf(a) ?? "this integration"}`}
              side="top"
            >
              <button
                type="button"
                aria-label={`Connect ${serverIdOf(a) ?? "integration"}`}
                onClick={() => onAction("openAuth", a)}
                data-action="openAuth"
                data-server-id={serverIdOf(a) ?? ""}
                style={{
                  ...iconButtonStyle,
                  background: senderTone(a.sender),
                  color: "#fff",
                }}
              >
                <LinkIcon size={15} />
              </button>
            </Tooltip>
          )}
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          alignItems: "center",
          justifyContent: "center",
          width: "clamp(58px, 17cqw, 68px)",
          gap: 0,
          padding: 2,
          borderRadius: 10,
          border: "1px solid var(--color-border, #2f2f2f)",
          background: "color-mix(in srgb, var(--color-surface, #1b1b1b) 72%, transparent)",
          flexShrink: 0,
        }}>
          {CONTROL_MODES.map(({ on, label, description, Icon }) => {
            const active = showControl === on;
            return (
              <Tooltip key={label} label={label} description={description} side="top">
                <button
                  type="button"
                  aria-label={label}
                  onClick={() => {
                    onAction("selected", a);
                    setShowControl(on);
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: 28,
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                    transition: "background 120ms, color 120ms",
                    background: active ? senderTone(a.sender) : "transparent",
                    color: active ? "#fff" : "var(--color-text-secondary, #6b7280)",
                  }}
                >
                  <Icon size={15} />
                </button>
              </Tooltip>
            );
          })}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, minWidth: 0 }}>
          {upgradeKnown && (
            <Tooltip
              label="Upgrade"
              description="A newer version is available. Compose and validate it now — until then this version is kept"
              side="top"
            >
              <button
                type="button"
                aria-label="Upgrade artifact to the newer version"
                onClick={upgradeArtifact}
                data-action="upgrade"
                data-artifact-id={artifactMachineId}
                style={{ ...iconButtonStyle, color: "var(--accent-primary, #6B7EEA)" }}
              >
                <CircleFadingArrowUp size={15} />
              </button>
            </Tooltip>
          )}
          {ACTIONS.map(({ key, Icon, label, description }) => (
            <Tooltip key={key} label={label} description={description} side="top">
              <button
                type="button"
                aria-label={label}
                onClick={() => onAction(key, a)}
                style={iconButtonStyle}
              >
                <Icon size={15} />
              </button>
            </Tooltip>
          ))}
        </div>
      </div>}

      <div style={{ display: "grid", gridTemplateRows: showControl ? undefined : "auto minmax(0, 1fr)", gap: fullscreen ? 0 : 12, minHeight: 0, overflow: "hidden" }}>
        {!fullscreen && !showControl && (
          <div style={{ padding: "0 12px" }}>
            <ReplyThenStrip a={a} />
          </div>
        )}
        {showControl
          ? (
            <div style={{ padding: "0 12px" }}>
              <ControlView a={a} artifactMachineId={artifactMachineId} query={query} onCancelArtifact={onCancelArtifact} onShareArtifact={onShareArtifact} />
            </div>
          )
          : artifactSurfaceSuppressed
          ? (
            <div
              style={suppressedArtifactStyle}
              data-artifact-state="stream-visible"
              data-artifact-id={artifactMachineId}
              aria-label={SUPPRESSED_ARTIFACT_BODY}
            >
              <strong style={suppressedArtifactTitleStyle}>{SUPPRESSED_ARTIFACT_TITLE}</strong>
              <span style={suppressedArtifactBodyStyle}>{SUPPRESSED_ARTIFACT_BODY}</span>
            </div>
          )
          : a.render.length > 0
          ? a.render.map((rf, i) => (
            <div key={i} style={{ display: "grid", gap: 4, minHeight: 0, height: "100%" }}>
              <ArtifactRenderer
                artifact={payloadArtifact}
                render={rf}
                title={String(title)}
                hydrateArtifact={hydrateArtifact}
                onHydratedArtifact={setHydratedArtifact}
                onArtifactInteraction={(interaction, detail) => onArtifactInteraction?.(interaction, a, detail)}
              />
            </div>
          ))
          : payloadArtifact
          ? (
            <ArtifactRenderer
              artifact={payloadArtifact}
              title={String(title)}
              hydrateArtifact={hydrateArtifact}
              onHydratedArtifact={setHydratedArtifact}
              onArtifactInteraction={(interaction, detail) => onArtifactInteraction?.(interaction, a, detail)}
            />
          )
          : a.error.length > 0
          ? (
            // Error boundary (sci/boundaries.md): a fail announcement
            // (announce:@error** — error:@machine:code + positions + refs)
            // RENDERS as a visible fail row; it is never a blank box.
            <div data-announcement-error style={{ display: "grid", gap: 6, padding: "0 12px" }}>
              {a.error.map((err, i) => (
                <div key={i} style={{
                  border: "1px solid var(--status-danger-border)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  display: "grid",
                  gap: 2,
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--status-danger-text)" }}>
                    Failed: {err.code}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--color-text-secondary)", wordBreak: "break-all" }}>
                    {err.machine}
                  </span>
                  {err.positions.length > 0 && (
                    <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                      at {err.positions.map((p) => p.path || JSON.stringify(p)).join(", ")}
                    </span>
                  )}
                  {a.notify?.reason && (
                    <span style={{ fontSize: 12, color: "var(--color-text-primary)", whiteSpace: "pre-wrap" }}>
                      {a.notify.reason}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )
          : msgText(a) && (
            // No render boxes → a plain text message (chat: user prompt / assistant
            // reply). Render the text so the message isn't an empty box.
            <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--color-text-primary)", whiteSpace: "pre-wrap", padding: "0 12px" }}>
              {msgText(a)}
            </div>
          )}
      </div>

      {!fullscreen && <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: 6,
        padding: "10px 12px 12px",
        minWidth: 0,
      }}>
        <span style={metaTagStyle} title={metaTitle}>
          <span style={{ ...metaTagLabelStyle, color: senderTone(a.sender) }}>
            {senderLabel(a.sender)}
          </span>
          <span style={metaTagValueStyle}>{String(title)}</span>
        </span>
        <span style={metaTagStyle} title={a.timestamp}>
          <span style={metaTagLabelStyle}>Time</span>
          <span style={metaTagValueStyle}>{humanizeChannelTime(a.timestamp)}</span>
        </span>
        {status ? (
          <StatusTag status={status} onRetry={() => onAction("retry", a)} />
        ) : (
          <span style={metaTagStyle} data-artifact-status="missing">
            <span style={metaTagLabelStyle}>Status</span>
            <span style={{ ...metaTagValueStyle, color: "var(--status-danger-text)" }}>Status missing</span>
          </span>
        )}
      </div>}
    </div>
  );
};
